import { UserRole } from '../models/organization/dto/invite-to-organization.dto';
import { RootPath } from './types';

/**
 * Pure role→navigation policy: constants and predicates only, NO dependency
 * on the admin route table. Leaf components and hooks import from HERE —
 * importing `./access` instead drags the entire page-component tree (and its
 * ESM-only deps) into every consumer's module graph, which breaks shallow
 * jest suites and bloats chunks. `./access` layers the route-table-aware
 * helpers on top and re-exports everything in this file.
 */

/**
 * Sidebar sections a COACH org member gets in the admin dashboard:
 * Calendrier / Terrains / Coachs. Everything else in `/admin` is admin-only.
 * Order matters: it is the sidebar order, and the first entry is the coach's
 * landing page.
 */
export const COACH_SIDEBAR_PATHS = ['/calendar', '/courts', '/coachs'] as const;

/**
 * Hidden detail pages the three coach sections navigate to. Without them a
 * coach clicking a court row or a calendar event would be bounced straight
 * back to the calendar by the layout's catch-all redirect.
 *
 * Keep this in sync with the `navigate('/admin/...')` calls in
 * `features/courts-calendar`, `features/court-management` and
 * `features/coach-management` (access.integration.test.ts scans those
 * directories).
 */
export const COACH_DETAIL_PATHS = [
    '/courtDetails',           // Terrains → a court
    '/coachDetails',           // Coachs → a coach (incl. their availabilities)
    '/coachingSessionDetails', // Calendrier → a coaching session
    '/gameDetails',            // Calendrier → a booked game
    '/eventDetails',           // Calendrier → an event
] as const;

/**
 * Every admin-layout path a coach may reach.
 *
 * NOTE: this is a NAVIGATION allowlist, not a security boundary. The bundle
 * ships every page to every signed-in user, so the API remains the only place
 * that can actually enforce what a coach may read or write (see
 * `api/src/libs/auth/has-organization-permissions.ts`).
 */
export const COACH_ADMIN_PATHS: readonly string[] = [
    ...COACH_SIDEBAR_PATHS,
    ...COACH_DETAIL_PATHS,
];

const ALL_PATHS = 'all' as const;

/**
 * Per-role navigation policy, deny-by-default: a role absent from this map —
 * or a future role added here without paths — can render nothing. Only ADMIN
 * gets the blanket grant; every restricted role must enumerate its paths.
 */
const ROLE_ADMIN_PATHS: Record<UserRole, readonly string[] | typeof ALL_PATHS> = {
    [UserRole.ADMIN]: ALL_PATHS,
    [UserRole.COACH]: COACH_ADMIN_PATHS,
};

/**
 * Roles admitted into the `/admin` layout at all. Referenced by App.tsx's
 * ProtectedRoute and pinned by access.test.ts — removing COACH here is
 * removing the coach dashboard.
 */
export const ADMIN_LAYOUT_ROLES: readonly UserRole[] = [UserRole.ADMIN, UserRole.COACH];

export function isCoach(role: UserRole): boolean {
    return role === UserRole.COACH;
}

/**
 * Can this role perform org-management actions (create/edit/delete resources)?
 * Drives action affordances (palette quick actions, create/delete buttons)
 * as opposed to page navigation. Mirrors the API's admin-role checks
 * (`hasOrganizationAdminAccess`) — keep the two in agreement, or the UI
 * offers buttons the API will 403.
 */
export function canManageOrgResources(role: UserRole): boolean {
    return !isCoach(role);
}

/** Can this role render the given admin-layout path (e.g. `/courts`)? */
export function canAccessAdminPath(role: UserRole, path: string): boolean {
    const allowed = ROLE_ADMIN_PATHS[role];
    if (allowed === ALL_PATHS) return true;
    return allowed ? allowed.includes(path) : false;
}

/**
 * Where this role should land after sign-in and on unknown admin paths.
 * The coach landing is derived from the allowlist head so the two can't
 * drift apart (a drift would turn the layout catch-all into a redirect loop).
 */
export function landingPathForRole(role: UserRole): string {
    return isCoach(role) ? `/admin${COACH_SIDEBAR_PATHS[0]}` : RootPath.ADMIN;
}
